import React from "react";

const btnBaseLight =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 transition-colors whitespace-nowrap";
const btnMutedLight =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap";
const btnPrimaryLight =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap";
const btnAccentLight =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors whitespace-nowrap";
const btnWarnLight =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors whitespace-nowrap";

const btnBaseDark =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-gray-600 text-white hover:bg-gray-500 transition-colors whitespace-nowrap";
const btnMutedDark =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors whitespace-nowrap";
const btnPrimaryDark =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors whitespace-nowrap";
const btnAccentDark =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors whitespace-nowrap";
const btnWarnDark =
  "px-2 py-1.5 text-xs sm:text-sm rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors whitespace-nowrap";

const closeLight =
  "px-3 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 transition-colors whitespace-nowrap shrink-0";
const closeDark =
  "px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm whitespace-nowrap shrink-0";

/**
 * Graph chrome: single header row with title and controls (including Close) on the right.
 */
const GraphHeader = ({
  title,
  centralNodeId,
  allNodes,
  onClose,
  tone = "dark",
  showToolbar = true,
  /** When set, replaces the default title (e.g. Explorer back button + label). */
  leading = null,
  /** Seamless strip inside a parent card (no extra header background band). */
  unifiedInCard = false,
  handleResetLayout,
  handleFilterToggle,
  handleTipsToggle,
  handleZoomIn,
  handleZoomOut,
  handleFitToView,
  showNavTips,
  showEntityTypesPanel,
  handleLoadConnections,
  selectedEntity,
}) => {
  const getNodeName = (node) => {
    if (!node || !node.name) return "Unknown Entity";
    if (React.isValidElement(node.name)) {
      try {
        return String(node.name.props?.children || "Unknown Entity");
      } catch {
        return "Unknown Entity";
      }
    }
    return String(node.name || "Unknown Entity");
  };

  const displayTitle =
    title ||
    (centralNodeId && allNodes.length > 0
      ? `Relationship Graph: ${getNodeName(allNodes.find((n) => n.id === centralNodeId))}`
      : "Graph Visualization");

  const isLight = tone === "light";

  const bl = isLight ? btnBaseLight : btnBaseDark;
  const ml = isLight ? btnMutedLight : btnMutedDark;
  const pr = isLight ? btnPrimaryLight : btnPrimaryDark;
  const ac = isLight ? btnAccentLight : btnAccentDark;
  const wn = isLight ? btnWarnLight : btnWarnDark;

  const stripClass =
    unifiedInCard && isLight
      ? "border-b border-slate-200 bg-slate-50 relative shrink-0"
      : unifiedInCard && !isLight
        ? "border-b border-gray-700 bg-gray-900 relative shrink-0"
        : isLight
          ? "bg-slate-100 border-b border-slate-200 relative shrink-0"
          : "bg-gray-800 border-b border-gray-700 relative shrink-0";

  return (
    <div className={stripClass} style={{ overflow: "visible" }}>
      <div
        className={`flex flex-wrap items-center gap-x-2 gap-y-2 min-w-0 ${
          unifiedInCard ? "p-2" : "p-3 md:p-4"
        }`}
      >
        {leading ? (
          <div className="hidden md:flex min-w-0 flex-1 basis-[min(100%,10rem)] items-center gap-2 sm:gap-3">
            {leading}
          </div>
        ) : (
          <h2
            className={
              isLight
                ? "text-base sm:text-lg md:text-xl font-semibold text-slate-900 min-w-0 flex-1 basis-[min(100%,12rem)] truncate"
                : "text-base sm:text-lg md:text-xl font-bold text-white min-w-0 flex-1 basis-[min(100%,12rem)] truncate"
            }
            title={displayTitle}
          >
            {displayTitle}
          </h2>
        )}

        <div className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-2 shrink-0 ml-auto max-w-full">
          {showToolbar ? (
            <>
              <button
                type="button"
                onClick={handleResetLayout}
                className={`${pr} hidden sm:inline-flex`}
                title="Reset layout"
              >
                Reset
              </button>
              {selectedEntity ? (
                <button
                  type="button"
                  onClick={handleLoadConnections}
                  className={`${ac} hidden sm:inline-flex`}
                  title="Load connected nodes"
                >
                  <span className="hidden sm:inline">Load connections</span>
                  <span className="sm:hidden">Load</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleFilterToggle}
                className={showEntityTypesPanel ? wn : ml}
                title="Filter entity types"
              >
                Filter
              </button>
              <button
                type="button"
                onClick={handleTipsToggle}
                className={showNavTips ? wn : ml}
                title="Navigation tips"
              >
                Tips
              </button>
              <span
                className={
                  isLight
                    ? "hidden md:inline text-xs text-slate-400 mx-0.5 select-none"
                    : "hidden md:inline text-xs text-gray-500 mx-0.5 select-none"
                }
                aria-hidden
              >
                |
              </span>
              <button type="button" onClick={handleZoomIn} className={bl} title="Zoom in">
                <span className="hidden sm:inline">Zoom in</span>
                <span className="sm:hidden">+</span>
              </button>
              <button type="button" onClick={handleZoomOut} className={bl} title="Zoom out">
                <span className="hidden sm:inline">Zoom out</span>
                <span className="sm:hidden">−</span>
              </button>
              <button type="button" onClick={handleFitToView} className={bl} title="Fit to view">
                <span className="hidden sm:inline">Fit to view</span>
                <span className="sm:hidden">Fit</span>
              </button>
            </>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className={isLight ? closeLight : closeDark}
              aria-label="Close graph"
            >
              Close
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GraphHeader;
